import React, { useEffect, useState, useRef, useCallback } from "react";
import { useShadowTechnique } from "../../context/ShadowTechniqueContext";
import { useVideo } from "../../context/VideoContext";
import "./ShadowProcessBox.css";
import ShadowTechniqueSettings from "./ShadowTechniqueSettings";
import ShadowTechniqueHelp from "./ShadowTechniqueHelp";

const ShadowProcessBox = () => {
  const {
    isActive,
    currentRepeat,
    repeatCount,
    startTime,
    endTime,
    isSettingStart,
    isSettingEnd,
    setIsSettingStart,
    setIsSettingEnd,
    setStartTime,
    setEndTime,
    startShadowTechnique,
    stopShadowTechnique,
    showProcessBox,
    setShowProcessBox,
  } = useShadowTechnique();

  const { videoRef } = useVideo();
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const [error, setError] = useState("");
  const [showSettings, setShowSettings] = useState(false); // اضافه شد
  const [showHelp, setShowHelp] = useState(false); // اضافه شد

  // تابع فرمت زمان
  const formatTime = useCallback((seconds) => {
    if (!seconds && seconds !== 0) return "--:--";

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // تعریف handlerها
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // useEffect برای مدیریت درگ
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // تابع تنظیم زمان شروع - بدون اعتبارسنجی
  const handleStartClick = useCallback(() => {
    if (videoRef.current) {
      const currentVideoTime = videoRef.current.currentTime;
      setStartTime(currentVideoTime);
      setError("");
    }
  }, [videoRef, setStartTime]);

  // تابع تنظیم زمان پایان - بدون اعتبارسنجی
  const handleEndClick = useCallback(() => {
    if (videoRef.current) {
      const currentVideoTime = videoRef.current.currentTime;
      setEndTime(currentVideoTime);
      setError("");
    }
  }, [videoRef, setEndTime]);

  // تابع اعتبارسنجی قبل از شروع
  const validateTimes = useCallback(() => {
    if (startTime === null || startTime === undefined) {
      setError("لطفاً زمان شروع را تنظیم کنید");
      return false;
    }

    if (endTime === null || endTime === undefined) {
      setError("لطفاً زمان پایان را تنظیم کنید");
      return false;
    }

    if (startTime >= endTime) {
      setError("زمان شروع باید قبل از زمان پایان باشد");
      return false;
    }

    return true;
  }, [startTime, endTime]);

  // شروع تکنیک سایه - با اعتبارسنجی کامل
  const handleStartTechnique = useCallback(() => {
    if (validateTimes()) {
      startShadowTechnique();
      setError("");
    } else {
      setTimeout(() => setError(""), 3000);
    }
  }, [startTime, endTime, repeatCount, startShadowTechnique, validateTimes]);

  const handleMouseDown = useCallback(
    (e) => {
      if (
        e.target.closest("button") ||
        e.target.closest(".process-header-actions")
      )
        return;
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position.x, position.y],
  );

  const handleClose = useCallback(() => {
    setShowProcessBox(false);
  }, [setShowProcessBox]);

  const handleBoxClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleSettingsClick = useCallback((e) => {
    e.stopPropagation();
    setShowSettings(true);
  }, []);

  const handleHelpClick = useCallback((e) => {
    e.stopPropagation();
    setShowHelp(true);
  }, []);

  // اگر قرار نیست باکس نمایش داده بشه، null برمی‌گردونیم
  if (!showProcessBox) return null;

  return (
    <>
      <div
        className="shadow-process-box"
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
        onClick={handleBoxClick}
      >
        <div className="shadow-process-header">
          <span className="shadow-process-title">🎯 تکنیک سایه</span>
          <div className="process-header-actions">
            <button
              className="process-header-btn settings-btn"
              onClick={handleSettingsClick}
              title="تنظیمات تکنیک سایه"
            >
              ⚙️
            </button>
            <button
              className="process-header-btn help-btn"
              onClick={handleHelpClick}
              title="راهنما"
            >
              ❓
            </button>
            <button
              className="process-header-btn close-btn"
              onClick={handleClose}
              title="بستن"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="shadow-process-content">
          {error && (
            <div
              className="error-message"
              style={{
                background: "rgba(244, 67, 54, 0.2)",
                color: "#f44336",
                padding: "8px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                textAlign: "center",
                marginBottom: "12px",
                border: "1px solid #f44336",
                fontWeight: "500",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              ⚠️ {error}
            </div>
          )}

          {!isActive ? (
            // حالت تنظیم
            <>
              <div
                className="time-setters"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="time-setter">
                  <span>شروع:</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartClick();
                    }}
                    className={
                      startTime !== null && startTime !== undefined ? "set" : ""
                    }
                  >
                    {startTime !== null && startTime !== undefined
                      ? formatTime(startTime)
                      : "تنظیم"}
                  </button>
                </div>
                <div className="time-setter">
                  <span>پایان:</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEndClick();
                    }}
                    className={
                      endTime !== null && endTime !== undefined ? "set" : ""
                    }
                  >
                    {endTime !== null && endTime !== undefined
                      ? formatTime(endTime)
                      : "تنظیم"}
                  </button>
                </div>
              </div>

              <div
                className="setting-hint"
                style={{
                  fontSize: "10px",
                  color: "#aaa",
                  textAlign: "center",
                  marginBottom: "15px",
                  fontStyle: "italic",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                ⏱️ با کلیک روی هر دکمه، زمان فعلی فیلم ثبت می‌شود
              </div>

              <button
                className="start-technique-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartTechnique();
                }}
              >
                شروع تکنیک سایه
              </button>
            </>
          ) : (
            // حالت اجرا
            <>
              <div
                className="repeat-counter"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="counter-label">تکرار</span>
                <span className="counter-value">
                  {currentRepeat} / {repeatCount}
                </span>
              </div>

              <button
                className="stop-technique-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  stopShadowTechnique();
                }}
              >
                توقف
              </button>
            </>
          )}
        </div>
      </div>

      {/* مودال تنظیمات */}
      {showSettings && (
        <ShadowTechniqueSettings onClose={() => setShowSettings(false)} />
      )}

      {/* مودال راهنما */}
      {showHelp && <ShadowTechniqueHelp onClose={() => setShowHelp(false)} />}
    </>
  );
};

export default ShadowProcessBox;
